// features/notes-feature.js — NOTES FEATURE
// ═══════════════════════════════════════════════════════════
// Unified capture surface for four note types: Note (typed),
// Voice (transcribed from the "Hey ai_name" wake word), Idea
// (auto-extracted from conversations), Highlight (text selection
// from chat messages).
//
// Structure mirrors Artifacts: Objects + Sessions tabs, search +
// Select + type filter toolbar. Click card → jump to origin thread
// for ideas/highlights; view content for notes/voice.
// ═══════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState } from 'react';
import ShiftPanelHeader from '../components/shift-panel-header';
import ShiftCard from '../components/shift-card';
import ThreadPickerModal from '../components/thread-picker-modal';
import useSelection from '../hooks/use-selection';
import { useNotes, NOTE_TYPE_COLORS, NOTE_TYPE_LABELS } from '../hooks/use-notes';
import { toNoteCard } from '../lib/card-presenters/note';
import ChatArtifactFeature from './chat-artifact-feature';

const TYPE_FILTER_OPTIONS = ['All types', 'Note', 'Voice', 'Idea', 'Highlight'];

export default function NotesFeature({
  userId, supabase, userRecord, onNoteNavigate, onClose,
}) {
  const { notes, loading, error, archiveNote, refresh } = useNotes({ userId, supabase });

  const [activeTab, setActiveTab] = useState('Objects');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [typeOpen, setTypeOpen] = useState(false);
  const [viewerNote, setViewerNote] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [activeSession, setActiveSession] = useState(null); // { sessionId, firstNote }

  const { selectMode, selectedItems, toggleSelectMode, toggleItem, clearSelection, isSelected } = useSelection();
  const typeRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (typeRef.current && !typeRef.current.contains(e.target)) setTypeOpen(false);
    }
    if (typeOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [typeOpen]);

  // ── Filtering ──────────────────────────────────────
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return notes.filter(n => {
      if (typeFilter && n.note_type.toLowerCase() !== typeFilter.toLowerCase()) return false;
      if (!q) return true;
      return (
        (n.title || '').toLowerCase().includes(q) ||
        (n.content || '').toLowerCase().includes(q) ||
        (n.source_text || '').toLowerCase().includes(q)
      );
    });
  }, [notes, searchQuery, typeFilter]);

  const pinned = filtered.filter(n => n.is_pinned);
  const recent = filtered.filter(n => !n.is_pinned);

  // ── Sessions grouping (Sessions tab) ───────────────
  const sessionsList = useMemo(() => {
    const groups = new Map();
    notes.forEach(n => {
      if (!n.session_id) return;
      if (!groups.has(n.session_id)) groups.set(n.session_id, []);
      groups.get(n.session_id).push(n);
    });
    return [...groups.entries()].map(([sessionId, list]) => {
      list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return { sessionId, notes: list, mostRecent: list[0]?.created_at || null };
    }).sort((a, b) => new Date(b.mostRecent) - new Date(a.mostRecent));
  }, [notes]);

  // ── Card handlers ──────────────────────────────────
  function handleCardOpen(note) {
    // Ideas + Highlights have an origin conversation — jump there.
    // Notes + Voice open in the inline viewer.
    if (note.note_type === 'idea' || note.note_type === 'highlight') {
      if (onNoteNavigate) onNoteNavigate(note);
      else setViewerNote(note);
    } else {
      setViewerNote(note);
    }
  }

  async function getAuthHeaders() {
    const { data: { session } } = await supabase.auth.getSession();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` };
  }

  // Shape notes as send-to-thread items. The endpoint is shared with
  // Artifacts; it only cares about { id, title, content, description }.
  function itemsFromSelection() {
    return selectedItems.map(n => ({
      id: n.id,
      title: n.title || defaultTitleFor(n),
      artifact_type: NOTE_TYPE_LABELS[n.note_type] || 'Note',
      content: n.content || '',
      description: n.source_text || (n.content || '').slice(0, 200),
    }));
  }

  async function handleNewThread() {
    try {
      const headers = await getAuthHeaders();
      const items = itemsFromSelection();
      const title = items.length === 1
        ? items[0].title
        : `${items.length} notes — ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      const description = items.length === 1
        ? items[0].description
        : `${items.length} notes: ${items.map(i => i.title).join(', ')}`;
      const res = await fetch('/api/user/send-to-thread', {
        method: 'POST', headers,
        body: JSON.stringify({ createNew: true, newTitle: title, description, items }),
      });
      if (res.ok) {
        const { threadId } = await res.json();
        clearSelection();
        setActiveSession({ sessionId: threadId, firstNote: selectedItems[0] });
      }
    } catch (e) { console.error('handleNewThread:', e); }
  }

  async function handlePickerSend(thread) {
    try {
      const headers = await getAuthHeaders();
      const items = itemsFromSelection();
      const res = await fetch('/api/user/send-to-thread', {
        method: 'POST', headers,
        body: JSON.stringify({ threadId: thread.id, items }),
      });
      if (res.ok) {
        clearSelection();
        setShowPicker(false);
      }
    } catch (e) { console.error('handlePickerSend:', e); }
  }

  // Per-type data mapping lives in lib/card-presenters/note.js.
  // The feature file only hands over handlers + selection state.
  const cardContext = {
    onOpen: handleCardOpen,
    onDelete: (note) => archiveNote(note.id),
    onSelect: toggleItem,
    selectMode,
    isSelected: (id) => isSelected(id),
    openLabelFor: originKindLabel,
  };
  function cardProps(n) { return toNoteCard(n, cardContext); }

  // ── Active synthesis thread takes over the panel ───
  if (activeSession) {
    return (
      <div className="shift-panel-overlay" style={{ display: 'flex', flexDirection: 'column' }}>
        <ChatArtifactFeature
          userId={userId} supabase={supabase} userRecord={userRecord}
          artifact={{ title: activeSession.firstNote?.title || 'Synthesis', artifact_type: 'Note' }}
          sessionId={activeSession.sessionId}
          onBack={() => { setActiveSession(null); refresh(); }}
        />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────
  return (
    <div className="shift-panel-overlay">
      <ShiftPanelHeader
        title="Notes"
        tagline="everything you have captured"
        taglineInline={true}
        tabs={[
          { label: 'Objects', color: '#c8a96e' },
          { label: 'Sessions', color: '#7a9e82' },
        ]}
        activeTab={activeTab}
        onTabChange={(tab) => { setActiveTab(tab); clearSelection(); setSearchQuery(''); setTypeFilter(''); }}
        onClose={onClose}
      />

      <div className="panel-content">
        <div className="cards-toolbar" style={{ borderBottom: '1px solid var(--border-1)', background: 'var(--surface-1)', flexShrink: 0 }}>
          <div className="cards-search-wrap">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="6.5" cy="6.5" r="5" stroke="var(--text-5)" strokeWidth="1.4"/>
              <path d="M10.5 10.5L14 14" stroke="var(--text-5)" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            <input type="text" className="cards-search"
              placeholder={activeTab === 'Sessions' ? 'Search sessions...' : 'Search notes...'}
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              onFocus={e => e.target.style.borderColor = 'var(--gold)'}
              onBlur={e => e.target.style.borderColor = 'var(--border-2)'} />
          </div>
          <div className="cards-toolbar-right">
            {activeTab === 'Objects' && (
              <button className={`cards-toolbar-select${selectMode ? ' active' : ''}`} onClick={toggleSelectMode}>Select</button>
            )}
            {activeTab === 'Objects' && (() => {
              const selectedTypeColor = typeFilter ? NOTE_TYPE_COLORS[typeFilter.toLowerCase()] : '#888888';
              return (
                <div ref={typeRef} style={{ position: 'relative' }}>
                  <button className="cards-toolbar-dropdown" onClick={() => setTypeOpen(!typeOpen)}
                    style={{ color: selectedTypeColor, borderColor: typeFilter ? selectedTypeColor : 'var(--border-2)' }}>
                    {typeFilter || 'All types'}
                    <svg width="8" height="5" viewBox="0 0 8 5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M1 1l3 3 3-3"/></svg>
                  </button>
                  {typeOpen && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: 'var(--surface-2)', border: '1px solid var(--border-2)', borderRadius: 4, zIndex: 50, minWidth: 130 }}>
                      {TYPE_FILTER_OPTIONS.map(t => {
                        const key = t.toLowerCase();
                        const itemColor = key === 'all types' ? '#888888' : NOTE_TYPE_COLORS[key];
                        const isActive = typeFilter === t || (t === 'All types' && !typeFilter);
                        return (
                          <div key={t} onClick={() => { setTypeFilter(t === 'All types' ? '' : t); setTypeOpen(false); }}
                            style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: isActive ? itemColor : '#888888', fontFamily: 'Georgia, serif', transition: 'color 0.15s', borderLeft: isActive ? `2px solid ${itemColor}` : '2px solid transparent' }}>
                            {t !== 'All types' && <div style={{ width: 7, height: 7, borderRadius: '50%', background: itemColor, flexShrink: 0 }} />}
                            {t}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {selectMode && selectedItems.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 16px', background: 'rgba(122,158,130,0.05)', borderBottom: '1px solid rgba(122,158,130,0.15)', flexShrink: 0 }}>
            <div style={{ flex: 1, fontSize: 12, color: 'var(--sage)', fontFamily: 'Georgia, serif' }}>
              {selectedItems.length} {selectedItems.length === 1 ? 'note' : 'notes'} selected
            </div>
            <button onClick={handleNewThread} style={{ background: 'transparent', border: '1px solid rgba(122,158,130,0.5)', color: 'var(--sage)', fontSize: 11, fontFamily: 'Georgia, serif', padding: '4px 12px', borderRadius: 3, cursor: 'pointer' }}>+ New thread</button>
            <button onClick={() => setShowPicker(true)} style={{ background: 'transparent', border: '1px solid var(--border-2)', color: 'var(--text-5)', fontSize: 11, fontFamily: 'Georgia, serif', padding: '4px 12px', borderRadius: 3, cursor: 'pointer' }}>Send to thread</button>
            <button
              onClick={clearSelection}
              onMouseEnter={e => e.currentTarget.style.color = '#c46050'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-6)'}
              style={{ background: 'none', border: 'none', color: 'var(--text-6)', cursor: 'pointer', fontSize: 11, fontFamily: 'Georgia, serif', transition: 'color 0.15s' }}
            >Cancel</button>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {loading && <EmptyText>loading···</EmptyText>}
          {!loading && error && <EmptyText>{error}</EmptyText>}

          {!loading && !error && activeTab === 'Objects' && filtered.length === 0 && (
            <EmptyText>
              {searchQuery || typeFilter
                ? 'No notes match your filters.'
                : 'No notes yet. Capture ideas from chat, highlight a message, or say "Hey Shift" to start a voice note.'}
            </EmptyText>
          )}

          {!loading && activeTab === 'Objects' && pinned.length > 0 && (
            <>
              <SectionLabel>Pinned</SectionLabel>
              <CardGrid items={pinned} cardProps={cardProps} />
            </>
          )}
          {!loading && activeTab === 'Objects' && recent.length > 0 && (
            <>
              {pinned.length > 0 && <SectionLabel>Recent</SectionLabel>}
              <CardGrid items={recent} cardProps={cardProps} />
            </>
          )}

          {!loading && activeTab === 'Sessions' && sessionsList.length === 0 && (
            <EmptyText>No session-linked notes yet.</EmptyText>
          )}
          {!loading && activeTab === 'Sessions' && sessionsList.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 400px))', gap: 40, justifyContent: 'center' }}>
              {sessionsList.map(g => (
                <ShiftCard key={g.sessionId}
                  item={{ id: g.sessionId, title: `${g.notes.length} note${g.notes.length === 1 ? '' : 's'} from this session`, created_at: g.mostRecent, summary_bullets: [] }}
                  accentColor="#7a9e82"
                  typeLabel="Session"
                  preview={g.notes.slice(0, 3).map(n => (n.title || defaultTitleFor(n))).join(' · ')}
                  actionLabel="View →"
                  onAction={() => {
                    const first = g.notes[0];
                    if (first && onNoteNavigate) onNoteNavigate(first);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {viewerNote && (
        <NoteViewerModal note={viewerNote} onClose={() => { setViewerNote(null); refresh(); }} />
      )}

      <ThreadPickerModal
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSend={handlePickerSend}
        selectedCount={selectedItems.length}
        supabase={supabase}
      />
    </div>
  );
}

// ── Small subcomponents ───────────────────────────────────
function EmptyText({ children }) {
  return (
    <div style={{ color: 'var(--text-7)', fontSize: 13, fontFamily: 'var(--font-serif)', textAlign: 'center', padding: '40px 0', fontStyle: 'italic' }}>
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ color: 'var(--text-7)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'var(--font-serif)', marginBottom: 12 }}>
      {children}
    </div>
  );
}

function CardGrid({ items, cardProps }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 400px))', gap: 40, justifyContent: 'center', marginBottom: 24 }}>
      {items.map(n => <ShiftCard {...cardProps(n)} />)}
    </div>
  );
}

// Minimal read-only viewer for Commit 1. Commit 4 replaces with a
// real editor that can rename, edit content, pin, and (for voice
// notes) send to a new thread.
function NoteViewerModal({ note, onClose }) {
  const accent = NOTE_TYPE_COLORS[note.note_type] || '#888';
  return (
    // shift-modal-overlay anchors the modal to the content area
    // (left: var(--sidebar-width)) so the sidebar doesn't hide the
    // left edge of the modal — this was the "popup too big / cut off"
    // bug where the modal centered on the full viewport but the
    // sidebar covered part of it.
    <div
      className="shift-modal-overlay"
      onClick={onClose}
      style={{
        zIndex: 200,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 560,
          maxHeight: 'calc(100vh - 80px)',
          background: '#07070a',
          border: `1px solid ${accent}55`,
          borderRadius: 12,
          padding: '24px 28px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, color: accent, fontFamily: 'Georgia, serif', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>
              {NOTE_TYPE_LABELS[note.note_type] || 'note'}
            </div>
            <div style={{ fontSize: 16, color: '#e4e4e4', fontFamily: 'Georgia, serif', lineHeight: 1.4 }}>
              {note.title || defaultTitleFor(note)}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#666',
              fontSize: 20,
              lineHeight: 1,
              padding: 4,
              cursor: 'pointer',
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#c46050'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#666'; }}
          >×</button>
        </div>

        {note.source_text && (
          <div style={{ fontSize: 12, color: '#888', fontFamily: 'Georgia, serif', fontStyle: 'italic', borderLeft: `2px solid ${accent}`, paddingLeft: 12, marginBottom: 12, flexShrink: 0 }}>
            {note.source_text}
          </div>
        )}

        <div style={{ fontSize: 14, color: '#cfcfcf', fontFamily: 'Georgia, serif', lineHeight: 1.65, overflowY: 'auto', whiteSpace: 'pre-wrap', flex: 1, minHeight: 0 }}>
          {note.content || <span style={{ color: '#555', fontStyle: 'italic' }}>Empty.</span>}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────
function defaultTitleFor(note) {
  if (note.title) return note.title;
  const src = note.content || note.source_text || '';
  const firstLine = src.split('\n').map(s => s.trim()).find(Boolean) || '';
  return firstLine.slice(0, 80) || `${NOTE_TYPE_LABELS[note.note_type] || 'note'}`;
}

function originKindLabel(note) {
  if (note.note_type === 'idea' || note.note_type === 'highlight') return 'View →';
  return 'Open →';
}
